/**
 * @file pwa/file-blob-url.ts
 * @description 把 fileId 解析成可用于 <img>/<a> 的 URL（preview/下载）。
 *
 * 解析优先级：
 *   1. cache_media 命中（id='file:<n>' 或 id='pending:<-n>' 或 status=uploaded 的 by-uploaded 索引）
 *      → URL.createObjectURL(blob)
 *   2. 在线 + 未命中 → GET /api/files/<id>/download?disposition=inline 拿 OSS 预签名
 *   3. 离线 + 未命中 → null（UI 显示「未下載」）
 *
 * 用于 ChecklistField / FieldFactory 注入 UploadHandlers.resolveBlobUrl，
 * 让附件真的能预览/下载，不只是名字。
 */
import { getOfflineDb } from '@/pwa/db';
import { apiClient } from '@/lib/api-client';

interface DownloadResponse {
  url: string;
}

/**
 * 解析 fileId 为可用 URL。返回 null 表示无法解析（离线 + 未缓存）。
 *
 * @param fileId 正数（服务器 file id）或负数（本地暂存 tempId）
 * @returns blob: 协议 URL 或 https URL；null 表示需要联网才能展示
 */
export async function resolveFileBlobUrl(fileId: number): Promise<string | null> {
  const db = await getOfflineDb();

  // 1) 优先查本地 cache_media
  if (fileId < 0) {
    // pending:<负数>
    const row = await db.get('cache_media', `pending:${fileId}`);
    if (row?.blob) return URL.createObjectURL(row.blob);
  } else {
    // 正数：先按 'file:<id>' 查（stage 3 cached）
    const cached = await db.get('cache_media', `file:${fileId}`);
    if (cached?.blob) return URL.createObjectURL(cached.blob);
    // 再按 by-uploaded 索引查（stage 2 上传成功未清理的 'pending:<-n>' 行）
    const uploaded = await db.getAllFromIndex('cache_media', 'by-uploaded', fileId);
    const first = uploaded[0];
    if (first?.blob) return URL.createObjectURL(first.blob);
  }

  // 2) 未命中
  // 离线 → 没法拿 OSS URL，返回 null 让 UI 标记
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return null;
  }
  // 负数 ID 不在服务器上，没法 fallback OSS
  if (fileId < 0) return null;

  // 3) 在线 fallback：调 download 端点拿预签名 URL
  try {
    const res = await apiClient.get<DownloadResponse>(
      `/api/files/${fileId}/download?disposition=inline`,
    );
    return res.url ?? null;
  } catch (err) {
    console.error('[file-blob-url] presign download failed', fileId, err);
    return null;
  }
}
